// Mirrors the Android app's PlayerMessageParser (app/.../playback/PlayerMessageParser.kt).
// The Vidking embed posts a PLAYER_EVENT envelope via window.parent.postMessage:
//   { type: "PLAYER_EVENT", data: { event, currentTime, duration, progress, id, mediaType, season, episode, timestamp } }
// Only the documented event names are accepted and values are validated.

export const PlayerEventType = {
  PLAY: "PLAY",
  PAUSE: "PAUSE",
  TIME_UPDATE: "TIME_UPDATE",
  SEEKED: "SEEKED",
  ENDED: "ENDED",
};

const EVENT_TYPES = {
  play: PlayerEventType.PLAY,
  pause: PlayerEventType.PAUSE,
  timeupdate: PlayerEventType.TIME_UPDATE,
  seeked: PlayerEventType.SEEKED,
  ended: PlayerEventType.ENDED,
};

function finiteNonNegative(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

export function parsePlayerEvent(json) {
  try {
    const envelope = typeof json === "string" ? JSON.parse(json) : json;
    if (!envelope || envelope.type !== "PLAYER_EVENT") return null;
    const data = envelope.data;
    if (!data) return null;

    const eventType = EVENT_TYPES[String(data.event || "").toLowerCase()];
    if (!eventType) return null;

    // VidLink posts the TMDB id as `mtmdbId` (their typo); Vidking used `id`.
    const rawId = data.id ?? data.mtmdbId;
    const id = parseInt(rawId, 10);
    if (!Number.isFinite(id)) return null;

    return {
      event: eventType,
      currentTimeSeconds: finiteNonNegative(data.currentTime),
      durationSeconds: finiteNonNegative(data.duration),
      tmdbId: id,
      mediaType: data.mediaType ?? null,
      season: data.season ?? null,
      episode: data.episode ?? null,
      timestamp: data.timestamp ?? null,
    };
  } catch {
    return null;
  }
}

// The embed and its sub-frames post messages; only accept messages from known
// player hosts. Vidking (dead Sep 2026) and its VidLink replacement
// (vidlink.pro) both post PLAYER_EVENT with the same shape; vidlink.pro is
// accepted for direct-embed playback. When the embed is served through our
// /api/vidking/ proxy it is same-origin with this app, so our own origin is
// accepted as well.
const VIDKING_HOST_RE = /(^|\.)(vidking\.net|vidlink\.pro)$/i;

export function isVidkingOrigin(origin) {
  if (!origin) return false;
  try {
    const host = new URL(origin).hostname;
    if (VIDKING_HOST_RE.test(host)) return true;
    if (typeof window !== "undefined" && origin === window.location.origin) return true;
    return false;
  } catch {
    return false;
  }
}